'use client';

import React, { useState, useEffect } from 'react';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'academy-first-visit-announcement-dismissed';

export interface FirstVisitAnnouncementCardProps {
  /** Card title */
  title?: string;
  /** Card message/body */
  message?: string;
  /** Optional CTA label (if provided, card can link somewhere) */
  ctaLabel?: string;
  /** Optional CTA URL */
  ctaUrl?: string;
  /** Override localStorage key (useful for multiple announcements) */
  storageKey?: string;
}

const defaultTitle = 'New Academy Experience';
const defaultMessage = 'We’ve reimagined the platform to provide a smoother learning experience.';
const defaultCtaLabel = 'Explore Now';
const defaultCtaUrl = '/academy/catalog';

export function FirstVisitAnnouncementCard({
  title = defaultTitle,
  message = defaultMessage,
  ctaLabel = defaultCtaLabel,
  ctaUrl = defaultCtaUrl,
  storageKey = STORAGE_KEY,
}: FirstVisitAnnouncementCardProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = localStorage.getItem(storageKey) === 'true';
    setIsVisible(!dismissed);
  }, [storageKey]);

  const handleClose = () => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      data-banner="first-visit"
      className="fixed bottom-4 left-4 z-90 w-[calc(100vw-2rem)] max-w-sm"
      role="region"
      aria-label="Announcement"
    >
      <div className="bg-surface-2 border-border-light relative flex flex-col gap-3 border px-4 py-4 shadow-sm">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="text-text-default-light hover:text-text-default-strong hover:bg-surface-4 absolute top-3 right-3 h-8 w-8 flex-shrink-0"
          aria-label="Dismiss announcement"
        >
          <MaterialIcons iconName="close" className="!text-[20px]" />
        </Button>
        <div className="min-w-0 pr-8">
          <h2 className="cs-h4 text-text-default-strong mb-1">{title}</h2>
          <p className="cs-body text-text-default-light mb-2">{message}</p>
        </div>
        <div className="flex w-full items-center gap-2">
          <Button
            variant="PrimaryFillButton"
            size="sm"
            asChild
            className="w-full min-w-0 flex-1 cursor-pointer"
          >
            <a href={ctaUrl} className="w-full justify-center" onClick={handleClose}>
              {ctaLabel}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
